import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { fetchWordPressBookById } from '@/lib/wordpress';
import { Book } from '@/types/book';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import LazyPDFReader from '@/components/LazyPDFReader';
import TxtReader from '@/components/TxtReader';
import AudioPlayer from '@/components/AudioPlayer';
import { 
  ArrowLeft, 
  FileText, 
  Volume2, 
  BookOpen, 
  AlertCircle,
  Loader2 
} from 'lucide-react';
import { toast } from 'sonner';

type ReaderFormat = 'pdf' | 'txt' | 'audio';

const Reader = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ReaderFormat>('pdf');

  // Get initial format from URL params or default to TXT (preferred) then PDF
  useEffect(() => {
    const format = searchParams.get('format') as ReaderFormat;
    if (format && ['pdf', 'txt', 'audio'].includes(format)) {
      setActiveTab(format);
    } else {
      // Default to TXT format if available, as it loads faster and avoids CORS issues
      if (book && book.txtUrl) {
        setActiveTab('txt');
        setSearchParams({ format: 'txt' });
      }
    }
  }, [searchParams, book]);

  useEffect(() => {
    const loadBook = async () => {
      if (!id) {
        setError('No book ID provided');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const fetchedBook = await fetchWordPressBookById(Number(id));
        if (fetchedBook) {
          setBook(fetchedBook);
          
          // Auto-select best available format (prioritize TXT > PDF > Audio)
          const urlFormat = searchParams.get('format');
          
          if (!urlFormat) {
            // No format specified, choose the best available option
            if (fetchedBook.txtUrl) {
              setActiveTab('txt');
              setSearchParams({ format: 'txt' });
              toast.success('Using fast-loading text reader');
            } else if (fetchedBook.pdfUrl) {
              setActiveTab('pdf');
              setSearchParams({ format: 'pdf' });
            } else if (fetchedBook.audioUrl) {
              setActiveTab('audio');
              setSearchParams({ format: 'audio' });
            }
          } else {
            // Format specified but not available, find best alternative
            if (activeTab === 'pdf' && !fetchedBook.pdfUrl) {
              if (fetchedBook.txtUrl) {
                setActiveTab('txt');
                setSearchParams({ format: 'txt' });
                toast.info('PDF not available, using text reader (faster)');
              } else if (fetchedBook.audioUrl) {
                setActiveTab('audio');
                setSearchParams({ format: 'audio' });
              }
            } else if (activeTab === 'txt' && !fetchedBook.txtUrl) {
              if (fetchedBook.pdfUrl) {
                setActiveTab('pdf');
                setSearchParams({ format: 'pdf' });
                toast.info('Text version not available, using PDF');
              } else if (fetchedBook.audioUrl) {
                setActiveTab('audio');
                setSearchParams({ format: 'audio' });
              }
            } else if (activeTab === 'audio' && !fetchedBook.audioUrl) {
              if (fetchedBook.txtUrl) {
                setActiveTab('txt');
                setSearchParams({ format: 'txt' });
                toast.info('Audio not available, using text reader');
              } else if (fetchedBook.pdfUrl) {
                setActiveTab('pdf');
                setSearchParams({ format: 'pdf' });
              }
            }
          }
        } else {
          setError('Book not found');
        }
      } catch (err) {
        console.error('Error loading book:', err);
        setError('Failed to load book details');
      } finally {
        setLoading(false);
      }
    };

    loadBook();
  }, [id]);

  const handleTabChange = (value: string) => {
    const format = value as ReaderFormat;
    setActiveTab(format);
    setSearchParams({ format });
  };

  const handleSwitchToText = () => {
    if (book?.txtUrl) {
      setActiveTab('txt');
      setSearchParams({ format: 'txt' });
      toast.success('Switched to text reader for faster loading');
    } else {
      toast.error('Text version not available for this book');
    }
  };

  const getAvailableFormats = (): { value: ReaderFormat; label: string; icon: React.ReactNode; available: boolean; recommended?: boolean }[] => [
    {
      value: 'txt',
      label: 'Text Reader',
      icon: <FileText className="h-4 w-4" />,
      available: !!book?.txtUrl,
      recommended: true // Prioritize text reader
    },
    {
      value: 'pdf',
      label: 'PDF Reader',
      icon: <BookOpen className="h-4 w-4" />,
      available: !!book?.pdfUrl
    },
    {
      value: 'audio',
      label: 'Audio Player',
      icon: <Volume2 className="h-4 w-4" />,
      available: !!book?.audioUrl
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-12">
          <Card className="max-w-md mx-auto">
            <CardContent className="p-8 text-center">
              <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Loading Book...</h3>
              <p className="text-muted-foreground">Fetching book details from server...</p>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !book) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-12">
          <Card className="max-w-md mx-auto">
            <CardContent className="p-8 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Error Loading Book</h3>
              <p className="text-muted-foreground mb-4">{error || 'Book not found'}</p>
              <Link to="/library">
                <Button>Back to Library</Button>
              </Link>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  const availableFormats = getAvailableFormats().filter(format => format.available);

  if (availableFormats.length === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-12">
          <Link to={`/book/${book.id}`}>
            <Button variant="ghost" className="mb-6">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Book Details
            </Button>
          </Link>
          
          <Card className="max-w-md mx-auto">
            <CardContent className="p-8 text-center">
              <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Reading Formats Available</h3>
              <p className="text-muted-foreground mb-4">
                This book doesn't have any available reading formats (PDF, Text, or Audio).
              </p>
              <Link to={`/book/${book.id}`}>
                <Button>Back to Book Details</Button>
              </Link>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-6">
        {/* Navigation */}
        <div className="flex items-center justify-between mb-6">
          <Link to={`/book/${book.id}`}>
            <Button variant="ghost" className="mb-2">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Book Details
            </Button>
          </Link>
        </div>

        {/* Book Info */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <img
                src={book.coverImage}
                alt={`${book.title} cover`}
                className="w-16 h-24 object-cover rounded"
              />
              <div className="flex-1">
                <Badge className="mb-2">{book.category}</Badge>
                <h1 className="text-2xl font-bold mb-1">{book.title}</h1>
                <p className="text-muted-foreground">by {book.author}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reader Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-1 sm:grid-cols-3 mb-6">
            {availableFormats.map(format => (
              <TabsTrigger 
                key={format.value}
                value={format.value}
                className="flex items-center gap-2"
              >
                {format.icon}
                {format.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* PDF Reader */}
          <TabsContent value="pdf">
            {book.pdfUrl ? (
              <LazyPDFReader
                url={book.pdfUrl}
                title={book.title}
                onSwitchToText={book.txtUrl ? handleSwitchToText : undefined}
              />
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">PDF Not Available</h3>
                  <p className="text-muted-foreground">This book doesn't have a PDF version available.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Text Reader */}
          <TabsContent value="txt">
            {book.txtUrl ? (
              <TxtReader
                url={book.txtUrl}
                title={book.title}
              />
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Text Version Not Available</h3>
                  <p className="text-muted-foreground">This book doesn't have a text version available.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Audio Player */}
          <TabsContent value="audio">
            {book.audioUrl ? (
              <AudioPlayer
                url={book.audioUrl}
                title={book.title}
              />
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Audio Not Available</h3>
                  <p className="text-muted-foreground">This book doesn't have an audio version available.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
};

export default Reader;